/**
 * Logger utility with localStorage persistence and file logging
 *
 * Provides structured logging with:
 * - Console output with styled prefixes
 * - localStorage persistence for debugging
 * - File logging via backend endpoint
 * - Export functionality for sharing logs
 * - Configurable log levels
 *
 * Usage in browser console:
 *   window.contextLogs.enable()     - Enable debug logging
 *   window.contextLogs.file(true)   - Enable file logging
 *   window.contextLogs.get()        - Get all logs
 *   window.contextLogs.export()     - Export logs to JSON file
 *   window.contextLogs.summary()    - Print logs summary
 */

import { getApiBaseUrl } from "@/config/apiBaseUrl";

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  module: string;
  message: string;
  data?: unknown;
}

const STORAGE_KEY = "context_debug_logs";
const FILE_LOG_ENABLED_KEY = "context_file_log";
const MAX_LOGS = 500;
const LOG_BATCH_SIZE = 10;
const LOG_FLUSH_INTERVAL = 5000; // 5 seconds
const MAX_BATCH_BYTES = 60 * 1024; // Below the server and browser keepalive limits.
const encoder = new TextEncoder();

// Buffer for file logging
let logBuffer: LogEntry[] = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<void> | null = null;

/**
 * Check if debug logging is enabled
 */
function isDebugEnabled(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem("context_debug") === "true";
}

/**
 * Check if file logging is enabled
 */
function isFileLogEnabled(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(FILE_LOG_ENABLED_KEY) === "true";
}

/**
 * Get stored logs from localStorage
 */
function getStoredLogs(): LogEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Save logs to localStorage
 */
function saveLogs(logs: LogEntry[]): void {
  if (typeof window === "undefined") return;
  try {
    // Keep only the last MAX_LOGS entries
    const trimmed = logs.slice(-MAX_LOGS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch (e) {
    console.warn("[Logger] Failed to save logs to localStorage:", e);
  }
}

/**
 * Flush log buffer to backend
 */
function takeLogBatch(): { logs: LogEntry[]; body: string } | null {
  const logs: LogEntry[] = [];
  let body = "";
  while (logBuffer.length > 0 && logs.length < LOG_BATCH_SIZE) {
    const entry = logBuffer[0];
    // An oversized record must not prevent later diagnostics from being sent.
    if (encoder.encode(entry.message).length > 16384 || encoder.encode(entry.module).length > 128) {
      logBuffer.shift();
      continue;
    }
    const candidate = JSON.stringify({
      logs: [...logs, entry],
      session_id: getSessionId(),
      user_agent: navigator.userAgent.slice(0, 512),
    });
    if (encoder.encode(candidate).length > MAX_BATCH_BYTES) {
      if (logs.length === 0) {
        logBuffer.shift();
        continue;
      }
      break;
    }
    logs.push(logBuffer.shift()!);
    body = candidate;
  }
  return logs.length > 0 ? { logs, body } : null;
}

async function flushLogsToBackend(): Promise<void> {
  if (inFlight) return inFlight;
  if (flushTimeout) {
    clearTimeout(flushTimeout);
    flushTimeout = null;
  }
  inFlight = (async () => {
    let batch;
    while ((batch = takeLogBatch())) {
      try {
        const response = await fetch(`${getApiBaseUrl()}/v2/debug/logs`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: batch.body,
          keepalive: true,
        });
        if (!response.ok && (response.status === 429 || response.status >= 500)) {
          throw new Error(`Log endpoint returned ${response.status}`);
        }
        // Permanent 4xx errors discard this batch; retrying it cannot fix the payload.
      } catch {
        logBuffer = [...batch.logs, ...logBuffer].slice(-MAX_LOGS);
        break;
      }
    }
  })().finally(() => {
    inFlight = null;
    if (logBuffer.length > 0 && isFileLogEnabled()) scheduleFlush();
  });
  return inFlight;
}

/**
 * Get or create a session ID for log grouping
 */
function getSessionId(): string {
  if (typeof window === "undefined") return "server";

  let sessionId = sessionStorage.getItem("context_log_session");
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem("context_log_session", sessionId);
  }
  return sessionId;
}

/**
 * Schedule log flush
 */
function scheduleFlush(): void {
  if (flushTimeout) return;

  flushTimeout = setTimeout(() => {
    flushTimeout = null;
    if (isFileLogEnabled()) {
      flushLogsToBackend();
    }
  }, LOG_FLUSH_INTERVAL);
}

/**
 * Add log to file buffer
 */
function addToFileBuffer(entry: LogEntry): void {
  logBuffer.push(entry);
  if (logBuffer.length > MAX_LOGS) logBuffer.splice(0, logBuffer.length - MAX_LOGS);

  // Flush immediately if buffer is full
  if (logBuffer.length >= LOG_BATCH_SIZE) {
    flushLogsToBackend();
  } else {
    scheduleFlush();
  }
}

/**
 * Add a log entry
 */
function addLogEntry(level: LogLevel, module: string, message: string, data?: unknown): void {
  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level,
    module,
    message,
    data: data !== undefined ? JSON.parse(JSON.stringify(data)) : undefined,
  };

  // Always log to console
  const prefix = `[${module}]`;
  const consoleMethod = level === "error" ? console.error : level === "warn" ? console.warn : console.log;

  if (data !== undefined) {
    consoleMethod(prefix, message, data);
  } else {
    consoleMethod(prefix, message);
  }

  // Save to localStorage if debug is enabled
  if (isDebugEnabled()) {
    const logs = getStoredLogs();
    logs.push(entry);
    saveLogs(logs);
  }

  // Add to file buffer if file logging is enabled
  if (isFileLogEnabled()) {
    addToFileBuffer(entry);
  }
}

/**
 * Create a logger for a specific module
 */
export function createLogger(module: string) {
  return {
    debug: (message: string, data?: unknown) => addLogEntry("debug", module, message, data),
    info: (message: string, data?: unknown) => addLogEntry("info", module, message, data),
    warn: (message: string, data?: unknown) => addLogEntry("warn", module, message, data),
    error: (message: string, data?: unknown) => addLogEntry("error", module, message, data),
  };
}

/**
 * Enable debug logging
 */
export function enableDebugLogs(): void {
  if (typeof window === "undefined") return;
  localStorage.setItem("context_debug", "true");
  console.log("[Logger] Debug logging enabled. Logs will be saved to localStorage.");
}

/**
 * Disable debug logging
 */
export function disableDebugLogs(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem("context_debug");
  console.log("[Logger] Debug logging disabled.");
}

/**
 * Enable/disable file logging
 */
export function setFileLogging(enabled: boolean): void {
  if (typeof window === "undefined") return;

  if (enabled) {
    localStorage.setItem(FILE_LOG_ENABLED_KEY, "true");
    console.log("[Logger] File logging enabled. Logs will be sent to backend.");
  } else {
    localStorage.removeItem(FILE_LOG_ENABLED_KEY);
    // Flush remaining logs before disabling
    if (logBuffer.length > 0) {
      flushLogsToBackend();
    }
    console.log("[Logger] File logging disabled.");
  }
}

/**
 * Force flush logs to backend
 */
export function forceFlush(): Promise<void> {
  return flushLogsToBackend();
}

/**
 * Clear stored logs
 */
export function clearLogs(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(STORAGE_KEY);
  logBuffer = [];
  console.log("[Logger] Logs cleared.");
}

/**
 * Get all stored logs
 */
export function getLogs(): LogEntry[] {
  return getStoredLogs();
}

/**
 * Export logs as JSON file
 */
export function exportLogs(): void {
  const logs = getStoredLogs();
  if (logs.length === 0) {
    console.warn("[Logger] No logs to export.");
    return;
  }

  const dataStr = JSON.stringify(logs, null, 2);
  const blob = new Blob([dataStr], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `context-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.json`;
  link.click();
  URL.revokeObjectURL(url);
  console.log(`[Logger] Exported ${logs.length} log entries.`);
}

/**
 * Get logs filtered by module
 */
export function getLogsByModule(module: string): LogEntry[] {
  return getStoredLogs().filter((log) => log.module === module);
}

/**
 * Get logs filtered by level
 */
export function getLogsByLevel(level: LogLevel): LogEntry[] {
  return getStoredLogs().filter((log) => log.level === level);
}

/**
 * Print logs summary to console
 */
export function printLogsSummary(): void {
  const logs = getStoredLogs();
  const byModule: Record<string, number> = {};
  const byLevel: Record<string, number> = {};

  logs.forEach((log) => {
    byModule[log.module] = (byModule[log.module] || 0) + 1;
    byLevel[log.level] = (byLevel[log.level] || 0) + 1;
  });

  console.group("[Logger] Summary");
  console.log(`Total logs: ${logs.length}`);
  console.log(`Session ID: ${getSessionId()}`);
  console.log(`File logging: ${isFileLogEnabled() ? "enabled" : "disabled"}`);
  console.log(`Buffer size: ${logBuffer.length}`);
  console.log("By module:", byModule);
  console.log("By level:", byLevel);
  if (logs.length > 0) {
    console.log("First log:", logs[0].timestamp);
    console.log("Last log:", logs[logs.length - 1].timestamp);
  }
  console.groupEnd();
}

/**
 * Get current logging status
 */
export function getStatus(): {
  debugEnabled: boolean;
  fileLogEnabled: boolean;
  sessionId: string;
  bufferSize: number;
  storedLogsCount: number;
} {
  return {
    debugEnabled: isDebugEnabled(),
    fileLogEnabled: isFileLogEnabled(),
    sessionId: getSessionId(),
    bufferSize: logBuffer.length,
    storedLogsCount: getStoredLogs().length,
  };
}

// Expose utilities to window for debugging in browser console
if (typeof window !== "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).contextLogs = {
    enable: enableDebugLogs,
    disable: disableDebugLogs,
    file: setFileLogging,
    flush: forceFlush,
    clear: clearLogs,
    get: getLogs,
    export: exportLogs,
    summary: printLogsSummary,
    status: getStatus,
    byModule: getLogsByModule,
    byLevel: getLogsByLevel,
  };

  // Auto-enable if previously enabled
  if (isDebugEnabled()) {
    console.log("[Logger] Debug logging is enabled. Use window.contextLogs to manage logs.");
  }
  if (isFileLogEnabled()) {
    console.log("[Logger] File logging is enabled. Logs will be sent to backend.");
  }

  // Flush logs on page unload
  window.addEventListener("beforeunload", () => {
    if (logBuffer.length > 0 && isFileLogEnabled()) {
      // Beacon is best effort and shares the browser's keepalive byte budget.
      const batch = takeLogBatch();
      if (batch) {
        try {
          if (navigator.sendBeacon(`${getApiBaseUrl()}/v2/debug/logs`, batch.body)) return;
        } catch {
          // Retain the batch if navigation is cancelled or the browser rejects it.
        }
        logBuffer = [...batch.logs, ...logBuffer].slice(-MAX_LOGS);
      }
    }
  });
}
